import React, { useEffect, useState, useRef } from 'react';
import ReactPlayer from 'react-player';
import './MediaPlayer.css';

interface MediaPlayerProps {
    src: string;
    type: 'audio' | 'video';
    source?: string;
}

const MediaPlayer: React.FC<MediaPlayerProps> = ({ src, type, source }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [volume, setVolume] = useState(0.8); // Default volume
    const [muted, setMuted] = useState(false);
    const [seekTime, setSeekTime] = useState(0);
    const playerRef = useRef<ReactPlayer>(null);
    const isHLS = src.endsWith('.m3u8');
    const containerRef = useRef<HTMLDivElement>(null);
    const [playerReady, setPlayerReady] = useState(false);

    const [preloadedSrc, setPreloadedSrc] = useState<string>(src);

    const preloadMP4 = async () => {
        try {
            const response = await fetch(src);
            if (!response.ok) throw new Error('Failed to fetch video');

            const blob = await response.blob();
            const objectURL = URL.createObjectURL(blob);
            setPreloadedSrc(objectURL);
        } catch (error) {
            console.error('Error preloading video:', error);
        }
    };

    useEffect(() => {
        if (!isHLS) preloadMP4(); // Only preload MP4 files
    }, [src]);

    useEffect(() => {
        if (playerReady && playerRef.current) {
            playerRef.current.seekTo(seekTime, 'seconds');
        }
    }, [seekTime, playerReady]);

    const handleError = (errorMessage: any) => {
        if (typeof errorMessage === 'string') setError(errorMessage);
        setIsPlaying(false);
    };

    const handleReady = () => {
        setPlayerReady(true);
        if (!isPlaying) {
            setIsPlaying(!isPlaying);
        }
    };
    const handlePause = () => {
        setIsPlaying(false);
    };
    const handlePlay = () => {
        setIsPlaying(true);
    };
    useEffect(() => {
        if (playerReady) {
            // Add a small delay before allowing seeking operations
            const timer = setTimeout(() => {
                if (containerRef.current) {
                    containerRef.current.focus();
                }
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [playerReady]);

    const handleSeek = (seconds: number) => {
        setSeekTime(seconds);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
        if (!playerRef.current || !playerReady) return; // Only proceed if player is ready
        const secondsLoaded = playerRef.current.getCurrentTime();
        switch (event.key) {
            case ' ':
                event.preventDefault();
                setIsPlaying((prev) => !prev); // Toggle play/pause
                break;
            case 'ArrowRight':
                handleSeek(secondsLoaded + 5); // Seek forward by 5 seconds
                break;
            case 'ArrowLeft':
                if (secondsLoaded > 5)
                    handleSeek(secondsLoaded - 5); // Seek backward by 5 seconds
                else handleSeek(-5);
                break;
            case 'ArrowUp':
                setVolume((prev) => Math.min(prev + 0.1, 1)); // Increase volume
                break;
            case 'ArrowDown':
                setVolume((prev) => Math.max(prev - 0.1, 0)); // Decrease volume
                break;
            case 'm':
            case 'M':
                setMuted((prev) => !prev); // Toggle mute
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            if (container) {
                container.removeEventListener('keydown', handleKeyDown);
            }
        };
    }, [playerReady]);

    useEffect(() => {
        // Focus directly on the ReactPlayer component's container (focusable)
        if (playerRef.current && playerRef.current.getInternalPlayer()) {
            playerRef.current.getInternalPlayer().focus(); // This focuses the internal player, which can accept keyboard inputs.
        }
    }, [src]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            if (playerRef.current) {
                playerRef.current.getInternalPlayer().focus();
            }
        };

        if (containerRef.current) {
            containerRef.current.addEventListener(
                'fullscreenchange',
                handleFullscreenChange
            );
        }

        return () => {
            if (containerRef.current) {
                containerRef.current.removeEventListener(
                    'fullscreenchange',
                    handleFullscreenChange
                );
            }
        };
    }, []);

    return (
        <div
            className={`media-player ${
                source && source === 'resource viewer' && 'z-50'
            }`}
            ref={containerRef}
            tabIndex={-1}
        >
            {type === 'video' ? (
                <ReactPlayer
                    key={preloadedSrc}
                    ref={playerRef}
                    url={preloadedSrc}
                    controls={true}
                    playing={isPlaying}
                    muted={muted}
                    volume={volume}
                    width="100%"
                    height={
                        source && source === 'resource viewer'
                            ? '100vh'
                            : 'auto'
                    }
                    config={{
                        file: {
                            forceVideo: true,
                            forceHLS: isHLS,
                            hlsOptions: {
                                startLevel: 1,
                                capLevelToPlayerSize: true,
                            },
                            attributes: {
                                controlsList: 'nodownload noremoteplayback',
                                disablePictureInPicture: true,
                                preload: 'auto',
                            },
                        },
                    }}
                    onReady={handleReady}
                    onError={(err) => {
                        handleError('Error playing video'); // Pass error to handler
                    }}
                    onClick={(e: any) => e.stopPropagation()}
                    onTouchEnd={(e: any) => e.stopPropagation()}
                    onSeek={(seconds: any) => handleSeek(seconds)}
                />
            ) : (
                <div
                    className={`flex flex-col justify-center items-center ${source && source === 'resource viewer' ? 'absolute top-[25%] left-[30%] min-w-[40rem] min-h-[20rem]' : 'relative  h-[15rem] rounded p-4'}`}
                >
                    <div
                        className="absolute h-full w-full top-0 left-0 opacity-30 rounded-[1rem]"
                        style={{
                            border: '1px solid #EFEFEF',
                            background:
                                'linear-gradient(90deg, rgba(215, 82, 237, 0.4) 0%, rgba(39, 91, 221, 0.6) 100%)',
                        }}
                    ></div>
                    <div className="playing">
                        <span
                            className={`playing__bar playing__bar1 ${isPlaying ? 'player_animation' : ''}`}
                        ></span>
                        <span
                            className={`playing__bar playing__bar2 ${isPlaying ? 'player_animation' : ''}`}
                        ></span>
                        <span
                            className={`playing__bar playing__bar3 ${isPlaying ? 'player_animation' : ''}`}
                        ></span>
                        <span
                            className={`playing__bar playing__bar4 ${isPlaying ? 'player_animation' : ''}`}
                        ></span>
                        <span
                            className={`playing__bar playing__bar1 ${isPlaying ? 'player_animation' : ''}`}
                        ></span>
                        <span
                            className={`playing__bar playing__bar2 ${isPlaying ? 'player_animation' : ''}`}
                        ></span>
                        <span
                            className={`playing__bar playing__bar3 ${isPlaying ? 'player_animation' : ''}`}
                        ></span>
                        <span
                            className={`playing__bar playing__bar4 ${isPlaying ? 'player_animation' : ''}`}
                        ></span>
                    </div>

                    <ReactPlayer
                        key={src}
                        ref={playerRef}
                        url={src}
                        controls={true}
                        width="80%"
                        height="50px"
                        muted={muted}
                        playing={isPlaying}
                        volume={volume}
                        onReady={handleReady}
                        onPause={handlePause}
                        onPlay={handlePlay}
                        onError={() => handleError('Failed to load audio')}
                        config={{
                            file: {
                                attributes: {
                                    controlsList: 'nodownload noremoteplayback',
                                    disablePictureInPicture: true,
                                    preload: 'auto',
                                },
                                forceAudio: true,
                            },
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default React.memo(MediaPlayer);
